import { Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../header/header.component';
import {
  ExcelImportService,
  ParseResult,
  ImportProgress,
  FullImportResult,
} from '../../services/excel-import.service';
import { ApiServiceService } from '../../services/api-service.service';
import { SectionInfo } from '../../Models/Question-details';

export interface ImportResult {
  success: boolean;
  sectionsCreated: number;
  fieldsCreated: number;
  errors: string[];
  fileName?: string;
}

@Component({
  selector: 'app-excel-import',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent],
  templateUrl: './excel-import.component.html',
  styleUrls: ['./excel-import.component.css']
})
export class ExcelImportComponent implements OnInit, OnDestroy {
  @Output() importComplete = new EventEmitter<ImportResult>();
  @Output() cancelled = new EventEmitter<void>();

  // UI State
  isVisible = false;
  isDragOver = false;
  // Preview: which field disclosures ("QUESTION ON FORM") are expanded — keyed "si:fi".
  expandedFields = new Set<string>();

  // AI generation
  aiMode = true;        // "Analyze with AI" is the default path; uncheck for classic import
  isAnalyzing = false;

  // AI conversational editing
  chatMessages: { role: 'user' | 'assistant'; text: string }[] = [];
  chatInput = '';
  isEditing = false;

  // "Save as a rule?" (long-term memory)
  ruleSuggestion: string | null = null;
  ruleSavedMessage: string | null = null;
  // false → rule applies to THIS form only; true → all the org's forms.
  ruleGlobal = false;

  // Process State
  currentStep: 'upload' | 'preview' | 'importing' | 'complete' = 'upload';

  // File data
  selectedFile: File | null = null;
  parseResult: ParseResult | null = null;
  uploadError: string | null = null;

  // Import progress
  importProgress: ImportProgress | null = null;
  importErrors: string[] = [];
  sectionsCreated = 0;
  fieldsCreated = 0;
  conditionalRulesCreated = 0;

  // Template ID
  templateId: string = '';
  // Form name (shown on every wizard screen, including Upload). Read LIVE from localStorage
  // 'Title' (populated from Partner.BaseForm when the form config loads) so it always shows
  // as soon as the title is available — no timing gap on the first screen.
  get formName(): string {
    return localStorage.getItem('Title') || '';
  }

  // Live-form editing: when the user opens the modal on a form that's ALREADY
  // saved and chooses "Edit current form with AI", we load that form into the
  // same preview + Form Assistant, and saving UPDATES it in place (no re-import,
  // no duplicates) instead of running a fresh import.
  editMode = false;
  existingSections: SectionInfo[] = [];
  // Set once a live edit has been saved, so closing the modal refreshes the page
  // to show the change in the Form Builder / Preview.
  formChangedDuringEdit = false;
  // Added/removed field counts for a delta re-import (shown on the complete step).
  deltaCount: { added: number; removed: number } | null = null;

  // Two Settings-tab feature flags:
  //  • aiFormBuilderEnabled  (isAiFormBuilderEnabled)  → AI extraction + attribute mapper + conditional logic
  //  • aiAssistantEnabled    (isAiFormAssistantEnabled) → AI Edit Agent (conversational editing + saved rules)
  // Flag 1 off → the "Analyze with AI" path is hidden (classic import only); with it
  //              go the attribute mapper and conditional logic (they ride with extraction).
  // Flag 2 off → chat / "Edit current form with AI" / "Save as a rule" hidden.
  aiFormBuilderEnabled = false;
  aiAssistantEnabled = false;

  constructor(
    private excelImportService: ExcelImportService,
    private apiService: ApiServiceService,
  ) {}

  ngOnInit() {
    this.templateId = localStorage.getItem('TemplateId') || '';
    this.loadAiFlags();
  }

  /** Read both Settings-tab flags via the existing GetSettingValue endpoint. */
  private loadAiFlags() {
    const isOn = (res: any) => String(res?.data ?? res?.Data ?? '').toLowerCase() === 'true';

    // Flag 1 — AI extraction + attribute mapper. Persisted so excel-import.service
    // can gate the attribute-mapper auto-population on import.
    this.apiService.GetSettingValue('isAiFormBuilderEnabled').subscribe({
      next: (res: any) => {
        this.aiFormBuilderEnabled = isOn(res);
        if (!this.aiFormBuilderEnabled) this.aiMode = false;   // no AI extraction → classic import
        try { localStorage.setItem('aiFormBuilderEnabled', this.aiFormBuilderEnabled ? 'true' : 'false'); } catch { /* ignore */ }
      },
      error: () => {
        this.aiFormBuilderEnabled = false; this.aiMode = false;
        try { localStorage.setItem('aiFormBuilderEnabled', 'false'); } catch { /* ignore */ }
      },
    });

    // Flag 2 — AI conditional logic + Edit Agent. Persisted so excel-import.service
    // can gate conditional logic on import.
    this.apiService.GetSettingValue('isAiFormAssistantEnabled').subscribe({
      next: (res: any) => {
        this.aiAssistantEnabled = isOn(res);
        try { localStorage.setItem('aiFormAssistantEnabled', this.aiAssistantEnabled ? 'true' : 'false'); } catch { /* ignore */ }
      },
      error: () => {
        this.aiAssistantEnabled = false;
        try { localStorage.setItem('aiFormAssistantEnabled', 'false'); } catch { /* ignore */ }
      },
    });
  }

  open() {
    this.templateId = localStorage.getItem('TemplateId') || '';
    // Preserve a prior session so the user can re-open and keep editing the
    // generated form (e.g. they missed something). The component may be destroyed
    // when previewing the form, so the draft is persisted to storage and restored
    // here. Only a deliberate "Change File" / start-over clears it.
    if (!this.parseResult) {
      this.reset();
      this.restoreDraft();
    }
    this.isVisible = true;
  }

  /**
   * Open at a FRESH Upload step — used by the Form Builder's "Return to Import" so a
   * re-import starts clean (new file) rather than restoring the previous draft.
   */
  openFresh() {
    this.templateId = localStorage.getItem('TemplateId') || '';
    this.reset();
    this.clearDraft();
    this.isVisible = true;
  }

  private draftKey(): string {
    return 'aiFormDraft:' + (this.templateId || localStorage.getItem('TemplateId') || '');
  }

  private persistDraft() {
    if (!this.parseResult) return;
    try {
      localStorage.setItem(this.draftKey(), JSON.stringify({
        parseResult: this.parseResult,
        chatMessages: this.chatMessages,
        currentStep: this.currentStep === 'importing' ? 'preview' : this.currentStep,
        fileName: this.selectedFile?.name ?? null,
      }));
    } catch { /* storage full / disabled — non-fatal */ }
  }

  private restoreDraft() {
    try {
      const raw = localStorage.getItem(this.draftKey());
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d?.parseResult) {
        this.parseResult = d.parseResult;
        this.chatMessages = Array.isArray(d.chatMessages) ? d.chatMessages : [];
        this.currentStep = d.currentStep === 'complete' ? 'complete' : 'preview';
      }
    } catch { /* corrupt draft — ignore */ }
  }

  private clearDraft() {
    try { localStorage.removeItem(this.draftKey()); } catch { /* ignore */ }
  }

  close() {
    this.persistDraft();   // keep the in-progress import so re-opening restores it
    this.isVisible = false;
    this.cancelled.emit();
    // Live edits are saved as you go; refresh so the Form Builder / Preview
    // reflect them.
    if (this.editMode && this.formChangedDuringEdit) {
      window.location.reload();
    }
  }

  /**
   * The form-builder renders this component inside an @if (preview toggle) that DESTROYS
   * it. Persist on teardown so re-opening Import restores the user's previous work
   * ("preserve previous work") instead of a blank upload screen.
   */
  ngOnDestroy() {
    this.persistDraft();
  }

  reset() {
    this.currentStep = 'upload';
    this.selectedFile = null;
    this.parseResult = null;
    this.uploadError = null;
    this.importProgress = null;
    this.importErrors = [];
    this.sectionsCreated = 0;
    this.fieldsCreated = 0;
    this.conditionalRulesCreated = 0;
    this.isDragOver = false;
    this.isAnalyzing = false;
    this.chatMessages = [];
    this.chatInput = '';
    this.isEditing = false;
    this.ruleSuggestion = null;
    this.ruleSavedMessage = null;
    this.ruleGlobal = false;
    this.editMode = false;
    this.existingSections = [];
    this.formChangedDuringEdit = false;
    this.deltaCount = null;
  }

  // --- Drag & drop ---

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private handleFile(file: File) {
    const MAX_BYTES = 25 * 1024 * 1024;   // 25 MB
    const supported = ['.xlsx', '.xls', '.xlsm', '.docx', '.doc', '.pdf', '.csv', '.txt'];
    const isValid = supported.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!isValid) {
      this.uploadError = 'Unsupported file format. Please upload a .pdf, .docx, .doc, .xlsx, .xls, .xlsm, .csv, or .txt file.';
      return;
    }
    // Reject empty / oversized files BEFORE any upload is initiated.
    if (!file.size) {
      this.uploadError = 'The file is empty. Please upload a document that contains content.';
      return;
    }
    if (file.size > MAX_BYTES) {
      this.uploadError = 'The file exceeds the 25 MB limit. Please upload a smaller file.';
      return;
    }
    this.uploadError = null;
    this.selectedFile = file;
    if (this.aiMode) {
      this.analyzeWithAI();
    } else {
      this.parseSelectedFile();
    }
  }

  /**
   * AI path: upload the raw file to the backend, which generates the form and
   * runs the relevance gate + guardrail. On success we feed the returned form
   * (already a ParseResult) straight into the existing preview + import flow.
   */
  private analyzeWithAI() {
    if (!this.selectedFile) return;

    this.isAnalyzing = true;
    this.uploadError = null;

    // EVERY file — including spreadsheets — goes through the LLM (AnalyzeTemplateAI).
    // Spreadsheet fidelity (real Yes/No options, all control rows) is handled on the
    // BACKEND, where the extractor now attaches each cell's data-validation dropdown
    // list to the text sent to the model, so the LLM sees the real choices instead of
    // the "Select Option" placeholder.
    this.apiService.AnalyzeTemplateAI(this.selectedFile).subscribe({
      next: (res: any) => {
        this.isAnalyzing = false;

        const assessment = res?.assessment;
        if (!assessment || assessment.isFormLike === false) {
          // Relevance gate turned it away — show why, create nothing.
          this.uploadError = assessment?.reason
            || "This file doesn't look like a form. Nothing was created.";
          return;
        }

        const form = res?.form as ParseResult;
        if (!form || !form.sections || form.sections.length === 0) {
          this.uploadError = 'The AI could not build a form from this file. Try classic import.';
          return;
        }

        // Safety net: if the AI returned no conditional logic (it sometimes misses
        // the reveal patterns), reconstruct the obvious Yes/gateway reveals from the
        // field-name hierarchy (Q5 → Q5_1, Q5_2 …) so sub-questions still start
        // hidden and reveal correctly. Conditional logic is part of the AI Form
        // Builder (Flag 1); it only runs when the builder produced this form.
        // Collapse a stack of single-Info preamble sections (Introduction / How to use /
        // Regulatory notes / …) into ONE intro section before anything else reads the
        // sections — universal, structure-based, 0% content loss.
        form.sections = this.excelImportService.mergeConsecutiveInfoSections(form.sections);

        // Build a DETERMINISTIC conditional-rule set: the name-hierarchy reveals
        // (scoping question qN → its sub-questions qNa/qNb…) are authoritative and the
        // same every run, and only genuinely-new AI rules (on questions the hierarchy
        // doesn't own) are merged. This removes the run-to-run rule-count swing.
        form.conditionalRules = this.excelImportService.buildConditionalRules(
          form.sections, form.conditionalRules || []
        );

        this.parseResult = form;
        this.currentStep = 'preview';
        this.persistDraft();
      },
      error: (err: any) => {
        this.isAnalyzing = false;
        // NEVER surface a raw technical error to the user (e.g. a 504 gateway page or
        // "Http failure response … 0 Unknown Error"). Only trust a message the BACKEND
        // deliberately wrote for end users; for everything else map the HTTP status to a
        // clean, friendly line.
        this.uploadError = this.friendlyAnalyzeError(err);
      }
    });
  }

  /**
   * Turn any AnalyzeTemplateAI failure into a user-safe message. A raw browser/gateway
   * error (status 0, HTML 504 body, stack text) must NEVER reach the UI.
   */
  private friendlyAnalyzeError(err: any): string {
    const status: number = typeof err?.status === 'number' ? err.status : 0;

    // 1) A message the backend WROTE FOR USERS. MapAiError returns { message, reference },
    //    and the CrowdStrike/file-type checks return a plain user-facing string. Trust a
    //    string only when it is short and contains no markup (a 504 body is HTML — reject it).
    const objMsg = typeof err?.error?.message === 'string' ? err.error.message : '';
    const rawStr = typeof err?.error === 'string' ? err.error : '';
    const cleanStr = rawStr && rawStr.length <= 300 && !/[<>]/.test(rawStr) ? rawStr : '';
    const trusted = objMsg || cleanStr;
    if (trusted) {
      const reference = err?.error?.reference;
      return reference ? `${trusted} (Ref: ${reference})` : trusted;
    }

    // 2) No trusted message — map the status to a friendly line. err.message is NEVER used.
    switch (status) {
      case 0:
        // status 0 = no readable response: usually a gateway timeout on a large document
        // (the 504 page is CORS-blocked so the browser reports 0), sometimes a dropped
        // connection. Name both, and point at the actionable cause.
        return 'We couldn’t get a response in time — the request may have timed out because the document is too large, or the connection dropped. Please try again, or upload a smaller document.';
      case 408:
      case 504:
        return 'The document took too long to process. Please try again, or upload a smaller file.';
      case 422:
        return 'This document is too large or complex to process in a single pass. Please try a smaller document.';
      case 503:
        return 'The AI service is busy right now. Please wait a moment and try again.';
      case 429:
        return 'Too many requests right now. Please wait a moment and try again.';
      case 413:
        return 'The file is too large. Please upload a smaller file.';
      case 401:
      case 403:
        return 'You’re not authorised to use the AI Form Builder, or it isn’t configured. Please contact your administrator.';
      case 502:
        return 'The AI service is temporarily unavailable. Please try again later.';
      default:
        return 'We couldn’t analyse this document. Please try again, or use classic import.';
    }
  }

  /**
   * Conversational edit: send the current form + instruction + prior turns to the
   * backend, then re-render the preview from the returned form and show the summary.
   */
  /**
   * Conservative client-side pre-filter: return true ONLY for input that is clearly
   * NOT a form edit (so we can refuse without an LLM call). Never blocks anything
   * that references editing — and when unsure, returns false so the LLM's guardrail
   * makes the call.
   */
  private looksOffTopic(text: string): boolean {
    const t = (text || '').trim().toLowerCase();
    if (!t) return false;

    // (1) WHITELIST — any hint of a real form edit → never block; hand to the LLM.
    //     Checked FIRST so a legitimate instruction is never rejected locally.
    const EDIT_SIGNAL = /\b(add|adds|adding|remove|removes|removing|delete|deletes|drop|rename|renames|make|makes|set|sets|change|changes|update|updates|edit|edits|modif(y|ies)|move|moves|insert|inserts|append|swap|reorder|duplicate|copy|clone|split|merge|group|mark|flag|clear|replace|require|required|mandatory|optional|option|options|dropdown|drop-?down|radio|checkbox|multi-?select|text\s*area|text\s*box|text\s*input|attachment|upload|signature|guidance|note|help\s*text|section|sub-?section|question|field|fields|label|title|placeholder|reveal|hide|show|conditional|condition|logic|trigger|expiry|expire|regex|expression|validation|validate|sequence|position|before|after|between|q\s*\d|section\s*\d)\b/;
    if (EDIT_SIGNAL.test(t)) return false;

    // (2) OFF-TOPIC — clearly not a form edit → refuse locally (no LLM call).
    if (t.endsWith('?')) return true;
    const OFF_TOPIC: RegExp[] = [
      // interrogatives / how-to questions
      /^(what|what'?s|how|why|who|whom|whose|when|where|which|is|are|am|can|could|would|should|do|does|did|will|shall)\b/,
      // general task verbs (write me…, translate…, install…)
      /^(explain|define|describe|tell\s+me|give\s+me|write|compose|generate|create\s+a|install|configure|set\s*up|calculate|compute|solve|translate|summari[sz]e|convert|fix|debug|help)\b/,
      // programming / tech topics
      /\b(java|python|javascript|typescript|c#|c\+\+|\.net|sql|html|css|angular|react|node|npm|git|docker|kubernetes|api|database|server|compiler|exception|stack\s*trace|algorithm|linux|windows)\b/,
      // general-knowledge / content requests
      /\b(recipe|poem|story|essay|joke|song|weather|news|meaning\s+of|capital\s+of|population\s+of)\b/,
      // prompt-injection / reveal internals / role change
      /\b(system\s*prompt|your\s+prompt|the\s+prompt|ignore\s+(all|previous|the|above)|disregard\s+(all|previous|the)|act\s+as|pretend|you\s+are\s+now|developer\s+mode|jailbreak|bypass)\b/,
      // greetings / meta / chit-chat
      /^(hi|hii|hey|hello|yo|sup|thanks|thank\s+you|thx|ok|okay|test|testing|who\s+are\s+you|what\s+can\s+you\s+do|your\s+name)\b/,
    ];
    if (OFF_TOPIC.some(re => re.test(t))) return true;

    // (3) Unsure → let the LLM's guardrail decide (safe default).
    return false;
  }

  /** Enter sends the instruction; Shift+Enter inserts a newline (multi-line composer). */
  onChatKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendInstruction();
    }
  }

  sendInstruction() {
    const instruction = (this.chatInput || '').trim();
    if (!instruction || !this.parseResult || this.isEditing) return;

    // History = prior turns only (the backend appends the current form + instruction).
    const history = this.chatMessages.map(m => ({ role: m.role, text: m.text }));
    this.chatMessages.push({ role: 'user', text: instruction });
    this.chatInput = '';

    // Short-circuit OBVIOUS off-topic input (knowledge questions, greetings, "how
    // to…") — refuse instantly WITHOUT a backend/LLM call. Anything that looks like
    // an edit, or is ambiguous, still goes to the LLM (the reliable guardrail).
    if (this.looksOffTopic(instruction)) {
      this.ruleSuggestion = null;
      this.ruleSavedMessage = null;
      this.chatMessages.push({
        role: 'assistant',
        text: "I can only help you edit this form — I can't help with that. Tell me what to change on the form.",
      });
      this.persistDraft();
      return;
    }

    this.isEditing = true;

    // templateId scopes standing-rule recall to this form (+ org-global rules).
    const before = this.formFingerprint(this.parseResult);
    const payload = { currentForm: this.parseResult, instruction, history, templateId: this.templateId };

    this.apiService.EditFormAI(payload).subscribe({
      next: (res: any) => {
        const updated = res?.updatedForm as ParseResult | undefined;
        if (updated) {
          this.parseResult = updated;
        }
        // Did the instruction ACTUALLY change the form? An off-topic / rejected /
        // no-op instruction returns the form unchanged. (Fingerprint compare is
        // tolerant of the backend's JSON round-trip.)
        const changed = !!updated && this.formFingerprint(updated) !== before;

        // Only offer to save the instruction as a standing rule when it actually
        // changed the form — never for a rejected off-topic request.
        this.ruleSuggestion = changed ? instruction : null;
        this.ruleSavedMessage = null;

        // Editing a LIVE form → apply & save the change right away, but ONLY when
        // something actually changed (don't re-save on a rejection/no-op).
        if (this.editMode && changed) {
          // Apply conditional logic too, but only if THIS instruction produced
          // condition rules (otherwise existing logic is left untouched).
          this.mergeAndSave(true, (result) => {
            this.isEditing = false;
            this.formChangedDuringEdit = true;
            this.chatMessages.push({
              role: 'assistant',
              text: result.success
                ? (res?.summary || 'Done.') + ' ✓ Applied to your form.'
                : 'Applied to the preview, but saving failed: ' + result.errors.join('; '),
            });
          });
        } else {
          // No change (rejection / no-op) or import-draft mode → just show the summary.
          this.isEditing = false;
          this.chatMessages.push({ role: 'assistant', text: res?.summary || 'Done.' });
          this.persistDraft();
        }
      },
      error: (err: any) => {
        this.isEditing = false;
        this.chatMessages.push({
          role: 'assistant',
          text: 'Sorry, I could not apply that change. ' + (err?.error?.message || err?.message || ''),
        });
      }
    });
  }

  /**
   * Stable content fingerprint of a form — the fields that matter for "did the edit
   * actually change anything?" (section titles, and per field: name/label/type/
   * required/options/helpText, plus conditional rules). Ignores JSON key order and
   * serialization differences from the backend round-trip, so a rejected/no-op
   * instruction fingerprints identically while a real edit (even toggling `required`)
   * differs.
   */
  private formFingerprint(pr: ParseResult | null): string {
    if (!pr?.sections) return '';
    const parts: string[] = [];
    for (const s of pr.sections) {
      parts.push('S:' + (s.title || ''));
      for (const f of (s.fields || [])) {
        parts.push([
          f.name, f.label, f.type, f.required ? 1 : 0,
          (f.options || []).join('|'), (f.helpText || ''),
        ].join('~'));
      }
    }
    for (const r of (pr.conditionalRules || [])) {
      parts.push('R:' + [
        r.sourceFieldName, r.triggerValue, r.targetFieldName,
        (r.mandatoryDeps || []).join('|'),
      ].join('~'));
    }
    return parts.join('\n');
  }

  /**
   * Diff the current (edited) form against the saved baseline, REUSING GUIDs so
   * SaveSection updates in place, then persist. Used by both live-form editing
   * (apply each instruction) and delta re-import (apply a re-uploaded file).
   * Adopts the saved structure as the new baseline so the next diff is correct.
   */
  private mergeAndSave(
    applyConditionalLogic: boolean,
    done: (result: { success: boolean; sectionsSaved: number; sectionsDeleted: number; errors: string[] }) => void
  ) {
    const merged = this.excelImportService.mergeEditedForm(
      this.existingSections,
      this.parseResult!,
      this.templateId
    );
    this.excelImportService.saveEditedForm(
      merged, this.existingSections, this.parseResult!, this.templateId, applyConditionalLogic
    ).subscribe({
      next: (result) => {
        // New baseline = what we just saved (so a follow-up edit diffs correctly).
        this.existingSections = JSON.parse(JSON.stringify(merged.sections));
        done(result);
      },
      error: (err: any) => {
        done({ success: false, sectionsSaved: 0, sectionsDeleted: 0, errors: [err?.message || 'Save failed'] });
      },
    });
  }

  /** Persist the last instruction as a standing org rule (long-term memory). */
  saveRule() {
    const instruction = this.ruleSuggestion;
    if (!instruction) return;
    this.apiService.SaveFormAiRule(instruction, this.templateId, this.ruleGlobal).subscribe({
      next: () => {
        this.ruleSavedMessage = this.ruleGlobal
          ? 'Saved — I\'ll apply this to all your forms.'
          : 'Saved — I\'ll apply this to this form in future.';
        this.ruleSuggestion = null;
      },
      error: () => {
        this.ruleSavedMessage = 'Could not save the rule. Please try again.';
      }
    });
  }

  /** Dismiss the save-as-rule prompt without saving. */
  dismissRule() {
    this.ruleSuggestion = null;
  }

  /** True when a form has already been saved for this template (so it can be edited). */
  get hasExistingForm(): boolean {
    return this.readExistingSections().length > 0;
  }

  /** Read the live/saved form (SectionInfo[]) from localStorage `AllQuestions`. */
  private readExistingSections(): SectionInfo[] {
    try {
      const raw = JSON.parse(localStorage.getItem('AllQuestions') || 'null');
      const sections = (raw?.[0]?.Section as SectionInfo[]) || [];
      // Only treat cached sections as "this form's" when they belong to the CURRENT
      // template — otherwise a fresh template could pick up another form's cached
      // sections and merge against the wrong GUIDs.
      const tid = this.templateId;
      const forThisTemplate = sections.filter(
        (s: any) => !tid || !s?.TemplateID || s.TemplateID === tid
      );
      return forThisTemplate;
    } catch {
      return [];
    }
  }

  /**
   * Live-form editing entry point. Loads the already-saved form into the SAME
   * preview + Form Assistant the import flow uses, but flagged so that saving
   * UPDATES the form in place (mergeEditedForm + saveEditedForm) instead of
   * importing a fresh copy. No file upload required.
   */
  editCurrentForm() {
    const existing = this.readExistingSections();
    if (!existing.length) {
      this.uploadError = 'There is no saved form to edit yet. Import or build a form first.';
      return;
    }
    this.uploadError = null;
    this.aiMode = true;
    this.editMode = true;
    this.existingSections = existing;
    this.selectedFile = null;
    this.parseResult = this.excelImportService.sectionsToParseResult(existing);
    this.chatMessages = [{
      role: 'assistant',
      text:
        'I\'ve loaded your current form. Tell me what to change — e.g. ' +
        '"add a dropdown \'Region\' with US, IND, Canada", "make CAGE code required", ' +
        'or "remove the Aerospace question". I\'ll update it in place.',
    }];
    this.currentStep = 'preview';
  }

  /** Fall back to the client-side heuristic parser for the selected file. */
  useClassicImport() {
    this.aiMode = false;
    this.uploadError = null;
    if (this.selectedFile) {
      this.parseSelectedFile();
    }
  }

  private parseSelectedFile() {
    if (!this.selectedFile) return;

    this.excelImportService.parseFile(this.selectedFile).subscribe({
      next: (result) => {
        this.parseResult = result;
        if (result.success) {
          this.currentStep = 'preview';
        } else {
          alert('Failed to parse file:\n' + result.errors.join('\n'));
        }
      },
      error: (err) => {
        alert('Error reading file: ' + err.message);
      }
    });
  }

  // --- Import ---

  startImport() {
    if (!this.parseResult || !this.templateId) {
      alert('Please select a file and ensure a template is selected');
      return;
    }

    this.currentStep = 'importing';
    this.sectionsCreated = 0;
    this.fieldsCreated = 0;
    this.importErrors = [];

    // A file (re)import is a clean REPLACE: replaceImport deletes EVERY section
    // currently on this template (from the backend — the source of truth), then
    // imports the uploaded file fresh. This removes old sections that a stale
    // localStorage merge can't see. (In-place, GUID-preserving editing is the
    // "Edit current form with AI" path, not re-upload.)
    this.deltaCount = null;
    this.excelImportService.replaceImport(
      this.parseResult.sections,
      this.parseResult.conditionalRules,
      this.templateId,
      (progress) => {
        this.importProgress = { ...progress };
        if (progress.status === 'success') {
          this.sectionsCreated = progress.currentSection;
        }
      }
    ).subscribe({
      next: (result: FullImportResult) => {
        this.currentStep = 'complete';
        this.sectionsCreated = result.sectionsCreated;
        this.fieldsCreated = result.fieldsCreated;
        this.conditionalRulesCreated = result.conditionalRulesCreated;
        this.importErrors = result.errors;

        this.importComplete.emit({
          success: result.success,
          sectionsCreated: result.sectionsCreated,
          fieldsCreated: result.fieldsCreated,
          errors: result.errors,
          fileName: this.selectedFile?.name || ''
        });

        // Persist the import summary (scoped to this template) so the Form Builder can
        // show the green "Imported …" banner AFTER goToFormEditor() reloads the page —
        // the in-memory state doesn't survive the reload, localStorage does.
        if (result.success) {
          try {
            localStorage.setItem('AIFB_ImportSummary', JSON.stringify({
              fileName: this.selectedFile?.name || '',
              sections: result.sectionsCreated,
              fields: result.fieldsCreated,
              templateId: this.templateId || localStorage.getItem('TemplateId') || ''
            }));
          } catch { /* localStorage may be unavailable; banner just won't persist */ }
        }
      },
      error: (err) => {
        this.currentStep = 'complete';
        this.importErrors = [err.message];
      }
    });
  }

  /** Count added / changed / removed fields between the saved form and a new one (for the delta summary). */
  private computeDeltaCount(existing: SectionInfo[], edited: ParseResult | null): { added: number; removed: number } {
    const norm = (s: string) => (s || '').trim().toLowerCase();
    const oldNames = new Set<string>();
    for (const s of (existing || [])) for (const f of ((s.Fields as any[]) || [])) oldNames.add(norm(f.Name));
    const newNames = new Set<string>();
    for (const s of (edited?.sections || [])) for (const f of s.fields) newNames.add(norm(f.name));
    let added = 0, removed = 0;
    newNames.forEach(n => { if (!oldNames.has(n)) added++; });
    oldNames.forEach(n => { if (!newNames.has(n)) removed++; });
    return { added, removed };
  }

  // --- Navigation ---

  goBack() {
    if (this.currentStep === 'preview') {
      this.clearDraft();          // deliberate start-over
      this.currentStep = 'upload';
      this.selectedFile = null;
      this.parseResult = null;
      this.chatMessages = [];
      this.editMode = false;
      this.existingSections = [];
    }
  }

  // --- Getters ---

  getTotalFields(): number {
    return this.parseResult?.totalFields || 0;
  }

  getSectionCount(): number {
    return this.parseResult?.sections.length || 0;
  }

  getConditionalRuleCount(): number {
    return this.parseResult?.conditionalRules?.length || 0;
  }

  // Plain-text version of a field label for the compact preview chips. Content blocks
  // (Info / Introduction) carry rich HTML in their label; strip tags so the list shows
  // readable text, not raw markup. The actual form still renders the HTML.
  labelText(label: string | null | undefined): string {
    return (label || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  }

  // --- Preview field disclosures ---

  /** Toggle the "QUESTION ON FORM" disclosure for a field (keyed by section + field index). */
  toggleField(si: number, fi: number) {
    const key = si + ':' + fi;
    if (this.expandedFields.has(key)) this.expandedFields.delete(key);
    else this.expandedFields.add(key);
  }

  isExpanded(si: number, fi: number): boolean {
    return this.expandedFields.has(si + ':' + fi);
  }

  /**
   * Clean, human-readable field label for the preview — no internal name / underscores.
   * Strips HTML and any leading numbering (e.g. "6b. ") so the list reads
   * "Certificate Number", not "cert_number" or "6b. Certificate Number".
   */
  displayLabel(field: any): string {
    let label = this.labelText(field?.label);
    if (!label) label = this.prettifyName(field?.name);
    return label.replace(/^\s*\d+[a-z]?[.)]\s*/i, '').trim() || 'Field';
  }

  /** The question shown to the end user on the form (help text / guidance). */
  fieldQuestion(field: any): string {
    return this.labelText(field?.helpText);
  }

  /** Short type badge for the preview (e.g. "TextInput" → "TEXT", matches the mockups). */
  typeBadge(field: any): string {
    const t = (field?.type || '').toLowerCase();
    if (t.includes('textarea')) return 'TEXT';
    if (t.includes('text') || t === 'textbox') return 'TEXT';
    if (t.includes('date')) return 'DATE';
    if (t.includes('radio')) return 'RADIO';
    if (t.includes('checkbox') || t === 'check') return 'CHECK';
    if (t.includes('dropdown') || t.includes('select')) return 'SELECT';
    if (t.includes('info') || t.includes('guidance') || t.includes('label')) return 'INFO';
    if (t.includes('attach')) return 'FILE';
    return (field?.type || 'TEXT').toString().toUpperCase().slice(0, 8);
  }

  /** "cert_number" → "Cert Number" fallback when no label is present. */
  private prettifyName(name: string | null | undefined): string {
    return (name || '')
      .replace(/[_\-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  /** Human file size for the preview file chip (e.g. "214 KB"). */
  get fileSizeText(): string {
    const bytes = this.selectedFile?.size || 0;
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /** Font-Awesome icon class for the uploaded file type. */
  get fileIconClass(): string {
    if (this.editMode) return 'fas fa-robot';
    const n = (this.selectedFile?.name || '').toLowerCase();
    if (n.endsWith('.pdf')) return 'fas fa-file-pdf';
    if (n.endsWith('.docx') || n.endsWith('.doc')) return 'fas fa-file-word';
    if (n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.xlsm')) return 'fas fa-file-excel';
    if (n.endsWith('.csv')) return 'fas fa-file-csv';
    return 'fas fa-file-alt';
  }

  /** Success screen: finish and land the user on the Form Editor showing the new form. */
  goToFormEditor() {
    this.isVisible = false;
    this.clearDraft();
    window.location.reload();
  }

  getProgressPercentage(): number {
    if (!this.importProgress || !this.parseResult) return 0;
    return Math.round((this.importProgress.currentSection / this.parseResult.sections.length) * 100);
  }

  // --- Sample template download ---

  downloadTemplate() {
    const sampleData = [
      ['Name', 'Label', 'Type', 'Required', 'Options', 'Section', 'HelpText', 'Condition'],
      ['Q1', 'Company Name', 'textbox', 'Yes', '', 'Company Information', 'Enter your company legal name', ''],
      ['Q2', 'Industry', 'dropdown', 'Yes', 'Technology,Finance,Healthcare,Manufacturing,Other', 'Company Information', 'Select your primary industry', ''],
      ['Q3', 'Effective Date', 'date', 'Yes', '', 'Company Information', '', ''],
      ['Q4', 'Do you agree to terms?', 'radio', 'Yes', 'Yes,No', 'Agreements', '', ''],
      ['Q5', 'Please explain', 'textarea', 'No', '', 'Agreements', '', 'if Q4 = Yes'],
      ['Q6', 'Additional Comments', 'textarea', 'No', '', 'Agreements', 'Any additional information', '']
    ];

    const ws = this.arrayToSheet(sampleData);
    const wb = { Sheets: { 'Form Template': ws }, SheetNames: ['Form Template'] };

    import('xlsx').then(XLSX => {
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'form_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    });
  }

  private arrayToSheet(data: any[][]): any {
    const sheet: any = {};
    const range = { s: { c: 0, r: 0 }, e: { c: 0, r: 0 } };

    for (let R = 0; R < data.length; R++) {
      for (let C = 0; C < data[R].length; C++) {
        if (range.e.r < R) range.e.r = R;
        if (range.e.c < C) range.e.c = C;
        const cellRef = this.encodeCell({ c: C, r: R });
        sheet[cellRef] = { v: data[R][C], t: 's' };
      }
    }
    sheet['!ref'] = this.encodeRange(range);
    return sheet;
  }

  private encodeCell(cell: { c: number; r: number }): string {
    let col = '';
    let c = cell.c;
    while (c >= 0) {
      col = String.fromCharCode((c % 26) + 65) + col;
      c = Math.floor(c / 26) - 1;
    }
    return col + (cell.r + 1);
  }

  private encodeRange(range: { s: { c: number; r: number }; e: { c: number; r: number } }): string {
    return this.encodeCell(range.s) + ':' + this.encodeCell(range.e);
  }
}
